import React from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { CacheOptions } from '@kurtosys/types/appsInitialize/index.js';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';

import { IApplicationEmbedConfiguration, IApplicationEmbedProps, IApplicationEmbedState } from './models/index.js';
import { APPLICATION_EMBED_CATEGORY_KEY, APPLICATION_EMBED_COMPONENT_KEY } from './ApplicationEmbed.meta.js';

export class ApplicationEmbed extends ComponentBase<
	IApplicationEmbedProps,
	IApplicationEmbedState,
	IApplicationEmbedConfiguration
> {
	categoryKey = APPLICATION_EMBED_CATEGORY_KEY;
	componentKey = APPLICATION_EMBED_COMPONENT_KEY;
	wrapperRef: React.RefObject<HTMLDivElement>;
	constructor(props: IApplicationEmbedProps) {
		super(props);
		this.wrapperRef = React.createRef();
	}
	get embedTarget(): HTMLElement | undefined {
		if (this.wrapperRef) {
			const wrapper = this.wrapperRef.current;
			if (wrapper && wrapper.children && wrapper.children.length > 0) {
				return wrapper.children[0] as HTMLElement;
			}
		}
		return undefined;
	}
	initialize() {
		const embedTarget = this.embedTarget;
		if (!embedTarget) {
			return;
		}
		this.attachListeners(embedTarget);
		const browserWindow = window as any;

		if (browserWindow && browserWindow.ksysAddEmbeddedApps) {
			browserWindow.ksysAddEmbeddedApps({
				embedTarget,
				hostname: this.props.hostname,
			});
		} else {
			// SCRIPT LOADING
			const hasManager = document.querySelectorAll('[data-ksys-app-template-id="ksys-app-manager"]').length > 0;
			if (this.props.forceScriptInject || !hasManager) {
				const { templateId, applicationCode, hostname, cacheOptions } = this.props;
				if (applicationCode && hostname) {
					const src = this.joinPath([
						hostname,
						'applicationManager',
						'apps',
						applicationCode,
						`main-${templateId}.js`,
					]);
					const scriptExists = document.querySelector('script[src|="' + src + '"]');
					if (!scriptExists) {
						const script = document.createElement('script');
						script.type = 'text/javascript';
						script.async = false;
						script.src = this.appendCacheOptionsQueryString(src, cacheOptions);
						document.body.appendChild(script);
					}
				}
			}
			// TRIGGER EVENT
			const { eventName = 'ksys-app-embed-added' } = this.props;
			if (browserWindow && browserWindow['ksysTriggerEvent']) {
				browserWindow['ksysTriggerEvent'](eventName, embedTarget);
			}
		}
	}
	appendCacheOptionsQueryString = (url: string, cacheOptions?: CacheOptions) => {
		let response = url;
		if (typeof cacheOptions === 'object') {
			const separator = url.includes('?') ? '&' : '?';
			const key = 'cache';
			response += `${separator}${key}=${common.commonUtils.stringifySafe(cacheOptions)}`;
		}
		return response;
	};
	joinPath = (pieces: string[]) => {
		return pieces
			.map((piece) => {
				let cleanPiece = piece;
				while (cleanPiece.startsWith('/')) {
					cleanPiece = cleanPiece.substr(1);
				}
				while (cleanPiece.endsWith('/')) {
					cleanPiece = cleanPiece.substr(0, cleanPiece.length - 1);
				}
				return cleanPiece;
			})
			.join('/');
	};
	appLoadCallback = () => {
		const { onLoad } = this.props;
		if (onLoad && this.embedTarget) {
			onLoad(this.embedTarget);
		}
	};
	attachListeners = (element: HTMLElement) => {
		const loadEventName = 'ksys-app-loaded';
		element.removeEventListener(loadEventName, this.appLoadCallback);
		element.addEventListener(loadEventName, this.appLoadCallback);
	};
	componentDidMount() {
		this.initialize();
	}
	componentDidUpdate() {
		this.initialize();
	}
	getInputKey = (rawKey: string) => {
		let key = rawKey;
		if (key.length > 1) {
			const firstLetter = key[0];
			const firstLetterUpper = firstLetter.toUpperCase();
			if (firstLetter !== firstLetterUpper) {
				key = [firstLetterUpper, key.substr(1)].join('');
			}
		} else {
			key = key.toUpperCase();
		}
		return `input${key}`;
	};
	render() {
		const {
			className,
			embedClassName,
			templateId,
			version,
			applicationCode,
			configurationKey = 'default',
			themeKey = 'default',
			styleKey = 'default',
			inputs = [],
			tag = 'div',
			render,
			embedId,
		} = this.props;
		if (!templateId) {
			return null;
		}
		const child = document.createElement(tag);
		if (embedClassName) {
			child.className = embedClassName;
		}
		child.dataset.ksysAppTemplateId = templateId;
		child.dataset.applicationEmbedComponent = 'true';
		if (version) {
			child.dataset.ksysAppTemplateVersion = version;
		}
		if (applicationCode) {
			child.dataset.applicationCode = applicationCode;
		}

		//The render for the application embed based on the attribute set by the grid or tabs application
		if (render) {
			child.dataset.render = render;
		}
		child.dataset.configurationKey = configurationKey;
		child.dataset.styleKey = styleKey;
		child.dataset.themeKey = themeKey;
		if (embedId) {
			child.dataset.embedId = embedId;
		}
		inputs.forEach((input) => {
			const { key: rawKey, value } = input;
			if (rawKey) {
				const inputKey = this.getInputKey(rawKey);
				child.dataset[inputKey] = value;
			}
		});
		const inputKeys = Object.keys(this.props).filter(
			(key) => key.startsWith('input') && key !== 'inputs',
		) as (keyof IApplicationEmbedProps)[];
		if (inputKeys && inputKeys.length > 0) {
			inputKeys.forEach((inputKey) => {
				child.dataset[inputKey] = this.props[inputKey];
			});
		}

		return (
			<div
				ref={this.wrapperRef}
				className={className}
				dangerouslySetInnerHTML={{ __html: child.outerHTML }}
			></div>
		);
	}
}

export default InjectedStyledComponent(
	observer(ApplicationEmbed),
	APPLICATION_EMBED_CATEGORY_KEY,
	APPLICATION_EMBED_COMPONENT_KEY,
);
